import { Dispatch, SetStateAction, useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useFormikContext } from 'formik'
import { css } from '@emotion/css'
import { useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { HelperWrapper } from 'src/components/helper-wrapper'
import { ETranslationNs, ICaptchaVerify, ISignupExisitingAccount, IUserSignupForm } from 'src/types'
import { AdditionalButton, Container, SubmitBtn, Texts, Title } from './AgreementStep.styled'
import { FONTS, TERMS_AND_CONDITIONS_URL } from 'src/constants'
import { TrackgoodLogo } from 'src/assets/icons/trackgood-logo.icon'
import { Loader } from 'src/components/loader'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'
import { BusinessCard } from './BusinessCard'

interface ISignupAgreementStepProps {
  captcha: ICaptchaVerify
  setStep: Dispatch<SetStateAction<number>>
  exisitngAccount?: ISignupExisitingAccount
}

export const AgreementStep = ({ setStep, exisitngAccount, captcha }: ISignupAgreementStepProps) => {
  const [t] = useTranslation()
  const { errors, values, handleChange, isSubmitting } = useFormikContext<IUserSignupForm>()
  const [searchParams] = useSearchParams()
  const withoutAccount = searchParams.get('without_account')
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile_s'))
  const [beenActive, setBeenActive] = useState<boolean>(false)
  const handleBackClick = () => {
    setStep(0)
  }

  return (
    <div
      className={css(
        Container,
        `height: ${isTablet ? '90vh' : '100%'};`,
        `width: 350px;`,
        isLaptop && `width: 500px; margin: auto;`,
        isTablet && `width: 300px;`,
        isMobile && `width: 90vw;`,
      )}
    >
      {' '}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px',
        }}
      >
        <TrackgoodLogo />
      </div>
      <div className={css(Texts, isMobile && `padding: 0px; width: 90%; gap: 0px;`)}>
        <p className={css(Title, isMobile && `font-size: 20px;`)}>
          {' '}
          {t('register-agreement-title-text.v')}
        </p>
      </div>
      <BusinessCard handleChange={handleChange} value={values.buisness}>
        <Collapse sx={{ width: '100%', py: '8px' }} in={!withoutAccount && values.buisness}>
          <TextInputWithHelper
            fullWidth
            inputName="account_holder"
            handleChange={(event) => {
              setBeenActive(true)
              handleChange(event)
            }}
            value={values.account_holder}
            error={beenActive && (errors.account_holder ? t(errors.account_holder + '.v') : '')}
            inputPlaceholder={t('sign-up-finish-account-name-input-placeholder.v')}
            inputUpperLabel={
              <HelperWrapper
                t={'sign-up-finish-account-name-input-label'}
                ns={ETranslationNs.Common}
                isInputLable
                isMandatory
              />
            }
          />
        </Collapse>
      </BusinessCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          width: '100%',
          ml: '15px',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={values.agreement}
              name="agreement"
              color="primary"
              id="agreement"
              onChange={handleChange}
              size="small"
              sx={{
                '& .MuiSvgIcon-root': { fontSize: FONTS.SIZES.xxl },
                '&.Mui-checked': {
                  color: theme.palette.custom.primary,
                },
              }}
            />
          }
          label={
            <span
              className={css(
                AdditionalButton,
                `color: ${theme.palette.custom.grey2}; margin: 5px;`,
                isTablet && `font-size: 12px;`,
              )}
            >
              <a
                href={TERMS_AND_CONDITIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={css`
                  color: inherit;
                  text-decoration: none;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                <HelperWrapper t="terms-checkbox-label" isMandatory />
              </a>
            </span>
          }
        />
      </Box>
      <Button
        disabled={(isSubmitting && captcha.verificationResp) || !!Object.keys(errors).length}
        type="submit"
        variant="contained"
        color="primary"
        className={css(SubmitBtn, isMobile && `width: 90%; position: relative;`)}
      >
        {isSubmitting && captcha.verificationResp ? (
          <Loader size={30} />
        ) : (
          <HelperWrapper t="sign-up-submit-button" />
        )}
      </Button>
      <Button
        onClick={handleBackClick}
        disabled={!!(errors.email || errors.password || !values.email || !values.password)}
        type="button"
        variant="outlined"
        className={css(SubmitBtn, isMobile && `width: 90%;`)}
      >
        <HelperWrapper t="sign-up-return-step-button" />
      </Button>
    </div>
  )
}
